import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'

import { ExpandedRows } from '../types/expandedRows'

interface ExpandedRowsContextState {
  expandedRows: ExpandedRows
  handleExpandCollapseAll: () => void
  isAllExpanded: boolean
  isRowExpanded: (id: string) => boolean
  toggleRow: (id: string) => void
}

const ExpandedRowsContext = createContext<ExpandedRowsContextState | undefined>(undefined)

interface ExpandedRowsContextProps {
  children: ReactNode
  allExpandableIds: string[]
}

export const ExpandedRowsContextProvider = ({ children, allExpandableIds }: ExpandedRowsContextProps) => {
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>({})

  const isAllExpanded = useMemo(
    () => allExpandableIds.length > 0 && allExpandableIds.every((id) => expandedRows[id]),
    [allExpandableIds, expandedRows],
  )

  const toggleRow = useCallback((id: string) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const isRowExpanded = useCallback((id: string) => !!expandedRows[id], [expandedRows])

  const expandAll = useCallback(() => {
    const newExpanded: Record<string, boolean> = {}
    for (const id of allExpandableIds) {
      newExpanded[id] = true
    }
    setExpandedRows(newExpanded)
  }, [allExpandableIds])

  const collapseAll = useCallback(() => {
    setExpandedRows({})
  }, [])

  const handleExpandCollapseAll = useCallback(() => {
    if (isAllExpanded) {
      collapseAll()
    } else {
      expandAll()
    }
  }, [isAllExpanded, collapseAll, expandAll])

  return (
    <ExpandedRowsContext.Provider
      value={{
        expandedRows,
        handleExpandCollapseAll,
        isAllExpanded,
        isRowExpanded,
        toggleRow,
      }}
    >
      {children}
    </ExpandedRowsContext.Provider>
  )
}

export const useExpandedRows = () => {
  const context = useContext(ExpandedRowsContext)

  if (!context) {
    throw new Error('ExpandedRowsContextProvider is missing in the module!')
  }

  return context
}
